import _ from 'lodash';
import { Link } from 'react-router-dom';
import { useContext, forwardRef, useImperativeHandle, useState } from "react";
import { RentTrackerContext } from '../../pages/tenants/rent-tracker';
import Comparer from "../../app/comparer";
import NumberHelpers from "../../app/number-helpers";

export const MonthLine = forwardRef(({ monthLine, index, handlePaymentAllocation, saveMonthLine, showSettled }, ref) => {

    const comparer = new Comparer();
    const numberHelpers = new NumberHelpers();

    const { getMonthYearString, myProfile } = useContext(RentTrackerContext);

    const [state, setState] = useState(monthLine);
    const [originalState, setOriginalState] = useState(structuredClone(monthLine));

    const totalDue = monthLine => monthLine.rent - monthLine.creditDebit;

    const totalReceivedInMonth = monthLine => {
        if (monthLine.received && monthLine.received > 0) {
            return monthLine.received.toFixed(2);
        }

        const total = paymentAllocation(monthLine);
        // Only apply toFixed if we have a valid number
        return total ? total.toFixed(2) : "0.00";
    };

    const paymentAllocation = monthLine => {
        return _.sumBy(monthLine.paymentAllocations, pa => {
            const value = pa.value;
            // Handle cases where value is a string ending with '.' or is an incomplete number
            if (typeof value === 'string') {
                if (value.endsWith('.') || value === '') {
                    return parseFloat(`${value}0`);
                }
                return parseFloat(value) || 0;
            }
            return value || 0;
        });
    };

    const hasChanges = () => {
        return !comparer.equals(originalState, state);
    };

    const canSave = () => {
        return hasChanges() && !!myProfile;
    };

    const cancel = () => {
        // Revert local state back to the original (clean) state
        setState(originalState);
        // Also update the shared rentData so summaries reflect the reverted value
        const originalValue = originalState.paymentAllocationStr ?? '';
        handlePaymentAllocation(originalValue, index);
    };

    const save = () => {

        const newMonthLine = structuredClone(state);

        const paIndex1 = newMonthLine.paymentAllocations.findIndex(pa => getMonthYearString(pa.date) === getMonthYearString(newMonthLine.period));
        if (paIndex1 > -1) {
            newMonthLine.paymentAllocations[paIndex1].value = numberHelpers.safeParseFloat(newMonthLine.paymentAllocationStr, 2);
            newMonthLine.paymentAllocations[paIndex1].createdBy ??= myProfile;
            newMonthLine.paymentAllocations[paIndex1].created ??= (new Date()).toISOString();
            newMonthLine.paymentAllocations[paIndex1].modifiedBy = myProfile;
            newMonthLine.paymentAllocations[paIndex1].modified = (new Date()).toISOString();
        }
        else {
            newMonthLine.paymentAllocations.push(
                {
                    value: numberHelpers.safeParseFloat(newMonthLine.paymentAllocationStr, 2),
                    date: (new Date(newMonthLine.period)).toISOString(),
                    createdBy: myProfile,
                    created: (new Date()).toISOString(),
                    modifiedBy: myProfile,
                    modified: (new Date()).toISOString()
                });
        }

        const paIndex2 = newMonthLine.tenant.currentTenancy.paymentAllocations.findIndex(pa => getMonthYearString(pa.date) === getMonthYearString(newMonthLine.period));
        if (paIndex2 > -1) {
            newMonthLine.tenant.currentTenancy.paymentAllocations[paIndex2].value = numberHelpers.safeParseFloat(newMonthLine.paymentAllocationStr, 2);
            newMonthLine.tenant.currentTenancy.paymentAllocations[paIndex2].createdBy ??= myProfile;
            newMonthLine.tenant.currentTenancy.paymentAllocations[paIndex2].created ??= (new Date()).toISOString();
            newMonthLine.tenant.currentTenancy.paymentAllocations[paIndex2].modifiedBy = myProfile;
            newMonthLine.tenant.currentTenancy.paymentAllocations[paIndex2].modified = (new Date()).toISOString();
        }
        else {
            newMonthLine.tenant.currentTenancy.paymentAllocations.push(
                {
                    value: numberHelpers.safeParseFloat(newMonthLine.paymentAllocationStr, 2),
                    //date: new Date(`${newMonthLine.period}-01`),
                    date: (new Date(newMonthLine.period)).toISOString(),
                    createdBy: myProfile,
                    created: (new Date()).toISOString(),
                    modifiedBy: myProfile,
                    modified: (new Date()).toISOString()
                });
        }

        saveMonthLine(newMonthLine, index);
    };

    // Expose methods to the parent component
    useImperativeHandle(ref, () => ({
        reset: newState => { setState(newState); setOriginalState(newState); },
        canSave: () => canSave()
    }));


    return !showSettled || (state && state.underOver && state.underOver < 0) ? (
        <tr key={`${state.tenant.id}-${index}`} className={`${state.underOver >= 0 ? "table-success" : "table-warning"}${state.saving ? "bg-indigo-200" : ""}`} >
            <td className="align-middle text-center" nowrap="true">{index + 1}</td>
            <td className="align-middle text-center" nowrap="true">{getMonthYearString(state.period)}</td>

            <td className="align-middle text-center" nowrap="true">
                {state.tenant.displayName} ({state.tenant.property.name})&nbsp;&nbsp;
                <Link to={`/tenant/detail/${state.tenant.id}`}>
                    <i className='fa fa-share-from-square' />
                </Link>

            </td>

            <td className="align-middle text-center" nowrap="true">{state.rent.toFixed(2)}</td>
            <td className="align-middle text-center" nowrap="true">{state.creditDebit.toFixed(2)}</td>
            <td className="align-middle text-center" nowrap="true">{totalDue(state).toFixed(2)}</td>
            <td className="align-middle text-center p-3px" nowrap="true">
                <input
                    className="align-middle text-center form-control ms-2px me-0"
                    style={{ maxWidth: '65px', width: '100%' }}
                    type="text"
                    inputMode="decimal"
                    placeholder="0"
                    value={state.paymentAllocationStr ?? ''}
                    onChange={e => {
                        const value = e.target.value;
                        // Allow optional leading minus, digits, and a single optional decimal point (supports in-progress decimals like "50.")
                        if (/^-?\d*\.?\d*$/.test(value)) {
                            setState({
                                ...state,
                                paymentAllocationStr: value,
                                underOver: numberHelpers.safeParseFloat(value, 2) - totalDue(state)
                            });
                            handlePaymentAllocation(value, index);
                        }
                    }}
                />
                {hasChanges() && (
                    <>
                        <button disabled={!canSave()} className="btn btn-primary mt-2" onClick={save}>
                            <i className="fa fa-save" />
                        </button>
                        <button className="btn btn-secondary mt-2 ms-2" onClick={cancel}>
                            Cancel
                        </button>
                    </>
                )}
            </td>
            <td className="align-middle text-center" nowrap="true">{totalReceivedInMonth(state)}</td>
            <td className="align-middle text-center" nowrap="true">{state.underOver.toFixed(2)}</td>
            <td className="align-middle text-center" nowrap="true">{state.tenant.balance.toFixed(2)}</td>
            <td className="align-middle text-center" nowrap="true">
                < div className={`badge bg-${state.underOver >= 0 ? 'success' : 'danger'}`}>
                    <i className={`fas fa-lg fa-fw fa-${state.underOver >= 0 ? 'check' : 'x'}`} />
                </div>
            </td>
        </tr>
    )
    :
    null;
});